import dayjs from 'dayjs';

import {
  InAccreditationItem,
  RequiredFile,
} from '../../../../../../api/theHive/redeProto/types';
import { AGING_BUCKETS } from '../../../../../../constants/redeProto';
import { protoColors } from '../../theme/tokens';

/**
 * Cor por ocupação do polo. Inverso de `heatColor` dos tokens: aqui percentual
 * alto é ruim (polo cheio, sem vaga para novos credenciados).
 */
export const occupancyColor = (pct: number): string => {
  if (pct >= 100) return protoColors.danger;
  if (pct >= 90) return protoColors.warning;
  if (pct >= 70) return protoColors.lime;
  return protoColors.success;
};

/**
 * Calendar days between the request and today, both in the browser's
 * timezone (the same day the table shows). `null` when missing or invalid.
 */
export const agingInDays = (requestedAt: string | null): number | null => {
  if (!requestedAt) return null;

  const requested = dayjs(requestedAt);
  if (!requested.isValid()) return null;

  const days = dayjs().startOf('day').diff(requested.startOf('day'), 'day');

  // Data futura (erro de cadastro) não vira aging negativo.
  return days < 0 ? 0 : days;
};

/** Faixa de espera do pedido; `null` quando não há data para classificar. */
export const agingBucketKey = (days: number | null): string | null => {
  if (days == null) return null;

  const bucket = AGING_BUCKETS.find(
    (item) => days >= item.min && (item.max == null || days <= item.max)
  );

  return bucket?.key ?? null;
};

/** Eixo completo = tem documento e todos aprovados. */
export const isAxisComplete = (files: RequiredFile[]): boolean =>
  files.length > 0 && files.every((file) => file.status === 'approved');

/** Percentual de documentos aprovados no eixo; `null` quando o eixo é vazio. */
export const axisApprovedPct = (files: RequiredFile[]): number | null => {
  if (files.length === 0) return null;
  const approved = files.filter((file) => file.status === 'approved').length;
  return (approved / files.length) * 100;
};

export interface AgingBucketCount {
  key: string;
  label: string;
  count: number;
}

export interface InAccreditationSummary {
  total: number;
  /** Média de dias de espera entre os pedidos que têm data. */
  avgAging: number | null;
  juridicoDone: number;
  engenhariaDone: number;
  abandoned: number;
  active: number;
  paying: number;
  agingBuckets: AgingBucketCount[];
  /** Pedidos sem `requested_at` — ficam fora das faixas e da média. */
  noDateCount: number;
}

/** Agregados do funil de credenciamento sobre a base completa do edital. */
export const summarize = (
  items: InAccreditationItem[]
): InAccreditationSummary => {
  const counts = new Map<string, number>();
  let agingSum = 0;
  let agingCount = 0;
  let noDateCount = 0;

  items.forEach((item) => {
    const days = agingInDays(item.requestedAt);
    if (days == null) {
      noDateCount += 1;
      return;
    }
    agingSum += days;
    agingCount += 1;

    const key = agingBucketKey(days);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  });

  return {
    total: items.length,
    avgAging: agingCount === 0 ? null : agingSum / agingCount,
    juridicoDone: items.filter((item) => isAxisComplete(item.juridico)).length,
    engenhariaDone: items.filter((item) => isAxisComplete(item.engenharia))
      .length,
    abandoned: items.filter((item) => item.abandoned).length,
    active: items.filter((item) => item.ativo === 'ativo').length,
    paying: items.filter((item) => item.plano != null).length,
    agingBuckets: AGING_BUCKETS.map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      count: counts.get(bucket.key) ?? 0,
    })),
    noDateCount,
  };
};
